import { Component, OnInit, OnDestroy } from '@angular/core';
import { ChartType } from 'chart.js';
import { DatePipe } from '@angular/common';
import { Subject, takeUntil, filter } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Priority, RtoCard, chartColors, RtoBarChartConstants, TeamCards } from '@app/shared/constants/global.enum';
import { TeamManagementService } from '../team-management.service';
import { AuthService } from '@core/auth.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
interface TeamCard {
  id: string;
  title: string;
  icon: string;
  color: string;
  type: 'metrics' | 'demographics' | 'coming-soon';
  data: { label: string; value?: string | number }[];
}

@Component({
  selector: 'app-team-management-row-two',
  templateUrl: './team-management-row-two.component.html',
  styleUrls: ['./team-management-row-two.component.scss']
})
export class TeamManagementRowTwoComponent implements OnInit, OnDestroy {
  chartType: ChartType = 'bar';
  employeeId: string | null = null;
  selectedDropdownValue: {
    employeeId: string;
    filterType?: string;
  } | null = null;
  barChartLabels: string[] = [];
  barChartData: any[] = [];
  barChartOptions: any;
  imageSrc: string = 'assets/icons/license.svg';
  priority = Priority;
  fontSize_opentab = 1.2;
  teamCards = TeamCards;

  loading: boolean = false;
  error: boolean = false;

  public RtoCard = RtoCard;
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private teamManagementService: TeamManagementService,
    private authService: AuthService,
    private toasterService: ToastrService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initChartOptions();
    this.buildLast12Months();

    // Subscribe to dropdown changes
    this.teamManagementService.getSelectedDropdownValue()
      .pipe(
        takeUntil(this.destroy$),
        filter(selection => selection !== null)
      )
      .subscribe((selection) => {
        this.selectedDropdownValue = selection;
        // Fetch data with the selected employee's ID and filterType from dropdown
        this.loadRtoData(selection!.employeeId, selection!.filterType);
      });

    this.initialiseParams();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onOpenNewClick(): void {
    this.router.navigate(['team-management/rto-details'], {
      state: {
        selectedDropdownValue: this.selectedDropdownValue,
      },
    });
  }

  private initialiseParams(): void {
    this.loading = true;
    this.error = false;

    this.authService.getEmployeeDetails()
      .then((profile: any) => {
        this.employeeId = profile?.employeeId || null;
        if (this.employeeId) {
          const currentSelection =
            this.teamManagementService.getCurrentSelectedDropdownValue();

          if (!currentSelection) {
            this.loadRtoData(this.employeeId);
          }
        } else {
          this.toasterService.error(RtoCard.employeeIdMissing);
        }
      })
      .catch((err: any) => {
        this.toasterService.error(RtoCard.noDataAvailable);
      });
  }

  private initChartOptions(): void {
    this.barChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 2, bottom: 0 } },
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: { boxWidth: 8, boxHeight: 8, font: { size: 10 }, padding: 8, color: '#6b7280' }
        },
        tooltip: {
          enabled: true,
          mode: 'index',
          intersect: false,
          backgroundColor: chartColors.backgroundWhite,
          titleColor: chartColors.textDark,
          bodyColor: chartColors.textDark,
          borderColor: '#e2e6eb',
          borderWidth: 1
        },
        datalabels: { display: false }
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          ticks: {
            autoSkip: false,
            maxRotation: 0,
            minRotation: 0,
            font: { size: 10 },
            color: '#6b7280',
            padding: 4,
            textAlign: 'center',
            callback: (value: any, index: any) => {
              const label = this.barChartLabels[index] || '';
              if (label) {
                const parts = label.split(' ');
                if (parts?.length === 2) {
                  return [parts[0], parts[1]];
                }
              }
              return label;
            }
          },
          categoryPercentage: 0.7,
          barPercentage: 0.8
        },
        y: {
          stacked: true,
          beginAtZero: true,
          min: 0,
          max: 100,
          grid: { color: '#f3f4f6', drawBorder: false },
          ticks: { stepSize: 20, precision: 0, font: { size: 11 }, color: '#6b7280' },
          afterFit: (scale: any) => { scale.width = 36; }
        }
      }
    };
  }

  private buildLast12Months(): void {
    const now = new Date();
    const dp = new DatePipe('en-US');
    const labels: string[] = [];

    for (let offset = 12; offset >= 1; offset--) {
      const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
      labels.push(dp.transform(date, 'MMM yyyy')?.trim() || '');
    }
    this.barChartLabels = labels;
  }

  private loadRtoData(employeeId: string, filterType?: string): void {
    this.teamManagementService.getRtoData(employeeId, filterType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const rtoData = response?.data || response || [];
          this.processChartData(rtoData);
        },
        error: (err: Error) => {
          this.toasterService.error(RtoCard.rtoDataFetchFailed);
        }
      });
  }

  private computeStackRadii(series: number[][], len: number): any[][] {
    const radii: any[][] = series.map(() => new Array(len).fill(0));
    const R = 4;
    for (let i = 0; i < len; i++) {
      const visible = series
        .map((s, idx) => ({ idx, val: s[i] }))
        .filter((s) => s.val > 0)
        .map((s) => s.idx);
      if (visible.length === 0) continue;
      const first = visible[0];
      const last = visible.at(-1)!;
      if (first === last) {
        radii[first][i] = R;
      } else {
        radii[first][i] = { topLeft: 0, topRight: 0, bottomLeft: R, bottomRight: R };
        radii[last][i] = { topLeft: R, topRight: R, bottomLeft: 0, bottomRight: 0 };
      }
    }
    return radii;
  }

  private processChartData(rtoData: any[]): void {
    const len = this.barChartLabels.length;
    const remote = new Array<number>(len).fill(0);
    const partialHybrid = new Array<number>(len).fill(0);
    const hybrid = new Array<number>(len).fill(0);
    const regular = new Array<number>(len).fill(0);

    const datePipe = new DatePipe('en-US');
    const monthMap = new Map<string, number>();
    this.barChartLabels.forEach((label, index) => {
      monthMap.set(label, index);
    });

    if (Array.isArray(rtoData)) {
      rtoData?.forEach(item => {
        if (item.month && typeof item.month === 'string') {
          const [yearStr, monthStr] = item?.month?.split('-');
          if (yearStr && monthStr) {
            const year = parseInt(yearStr);
            const month = parseInt(monthStr);

            const date = new Date(year, month - 1);
            const monthLabel = datePipe?.transform(date, 'MMM yyyy');

            if (monthLabel && monthMap.has(monthLabel)) {
              const index = monthMap.get(monthLabel)!;

              remote[index] += Number(item.remotePercentage || 0);
              partialHybrid[index] += Number(item.partialHybridPercentage || 0);
              hybrid[index] += Number(item.hybridPercentage || 0);
              regular[index] += Number(item.regularPercentage || 0);
            }
          }
        }
      });
    }
    const baseStyle = { stack: 'rto', borderSkipped: false, maxBarThickness: 32 };
    const radii = this.computeStackRadii([remote, partialHybrid, hybrid, regular], len);
    this.barChartData = [
      { data: remote, label: RtoCard.remote, backgroundColor: chartColors.rtoRed, borderRadius: radii[0], ...baseStyle },
      { data: partialHybrid, label: RtoCard.partialHybrid, backgroundColor: chartColors.rtoLightRed, borderRadius: radii[1], ...baseStyle },
      { data: hybrid, label: RtoCard.hybrid, backgroundColor: chartColors.rtoLightGreen, borderRadius: radii[2], ...baseStyle },
      { data: regular, label: RtoCard.regular, backgroundColor: chartColors.rtoDarkGreen, borderRadius: radii[3], ...baseStyle }
    ];
  }
}
